import React, { useState, useEffect } from "react";
import { LLM_OPTIONS, LLM_PROVIDERS } from "@/shared/utils/onboardingConfig";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { ArrowRight } from "lucide-react";
import { useUpdateLlmSettings } from "../../hooks/useLlmSettings";
import { showError } from "@/shared/utils/toast";
import {
  saveOnboardingLlmDraft,
  readOnboardingLlmDraft,
} from "../../utils/onboardingLlmDraftStorage";
import { useMainStore } from "@/stores";

// display mask for api key last 4
function maskApiKeyLast4(last4) {
  if (!last4) return "";
  return `sk-•••••••••••••••••••${last4}`;
}

const LLMSelection = ({ setCurrentStep }) => {
  const [selectedLLM, setSelectedLLM] = useState(LLM_OPTIONS[0].id);
  const [selectedProvider, setSelectedProvider] = useState(LLM_PROVIDERS[0].id);
  const [apiKey, setApiKey] = useState("");
  /** "empty" | "masked" (show last-4 mask until user focuses/types) | "editing" */
  const [keyDisplayMode, setKeyDisplayMode] = useState("empty");
  const [savedKeyLast4, setSavedKeyLast4] = useState(null);
  const setUser = useMainStore((s) => s.setUser);
  const { mutateAsync, isPending } = useUpdateLlmSettings();

  const isByoLlm = selectedLLM === "bring_your_own_llm";

  useEffect(() => {
    const meta = LLM_PROVIDERS.find((p) => p.id === selectedProvider);
    if (meta?.disabled) setSelectedProvider("openai");
  }, [selectedProvider]);

  useEffect(() => {
    const draft = readOnboardingLlmDraft();
    if (draft?.llm_type) setSelectedLLM(draft.llm_type);
    if (draft?.llm_provider) {
      const meta = LLM_PROVIDERS.find((p) => p.id === draft.llm_provider);
      setSelectedProvider(meta?.disabled ? "openai" : draft.llm_provider);
    }
  }, []);

  useEffect(() => {
    if (!isByoLlm) {
      setKeyDisplayMode("empty");
      setSavedKeyLast4(null);
      setApiKey("");
      return;
    }
    const draft = readOnboardingLlmDraft();
    const last4 = draft?.openai_key?.last_4;
    if (
      draft?.llm_type === "bring_your_own_llm" &&
      draft.openai_key?.exists &&
      last4
    ) {
      setSavedKeyLast4(last4);
      setKeyDisplayMode("masked");
      setApiKey("");
    } else {
      setSavedKeyLast4(null);
      setKeyDisplayMode("empty");
      setApiKey("");
    }
  }, [isByoLlm]);

  const handleClick = (id) => {
    setSelectedLLM(id);
  };

  const handleKeyFocus = () => {
    if (keyDisplayMode === "masked") {
      setKeyDisplayMode("editing");
      setApiKey("");
    }
  };

  const handleKeyChange = (event) => {
    setKeyDisplayMode("editing");
    setApiKey(event.target.value);
  };

  const apiKeyInputValue =
    keyDisplayMode === "masked" && savedKeyLast4
      ? maskApiKeyLast4(savedKeyLast4)
      : apiKey;

  const inputType =
    keyDisplayMode === "masked" && savedKeyLast4 ? "text" : "password";

  const handleContinue = async () => {
    const reusingSavedKey =
      isByoLlm && keyDisplayMode === "masked" && !!savedKeyLast4;

    if (isByoLlm && !reusingSavedKey) {
      if (!apiKey.trim()) {
        showError("API key is required for Bring Your Own LLM.");
        return;
      }
      if (!apiKey.startsWith("sk-") || apiKey.length < 20) {
        showError(
          "Enter a valid OpenAI API key (sk-…, at least 20 characters).",
        );
        return;
      }
    }

    if (isByoLlm && !selectedProvider) {
      showError("Please select an LLM provider.");
      return;
    }

    const payload = {
      llm_type: selectedLLM,
      llm_provider: isByoLlm ? selectedProvider : null,
      is_byo_llm_enabled: isByoLlm,
      api_key: isByoLlm ? (reusingSavedKey ? null : apiKey.trim()) : null,
    };

    try {
      const data = await mutateAsync(payload);
      saveOnboardingLlmDraft(data);
      setUser({ organization_llm_type: data?.llm_type ?? selectedLLM });
      setCurrentStep(2);
    } catch {
      /* toast from hook */
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-2">
          <h2 className="text-gray-800 font-bold text-2xl">
            Choose Your AI Setup
          </h2>
          <p className="text-gray-500 text-sm">
            Select how you’d like Salesworx to power AI workflows, insights, and
            outreach assistances
          </p>
        </div>

        <div className="flex flex-col gap-4 w-full">
          {LLM_OPTIONS?.map((option) => (
            <div
              key={option.id}
              className={`flex border border-gray-200 rounded-lg px-6 py-4 flex-col gap-2 hover:shadow cursor-pointer duration-500 transition-all ${selectedLLM === option.id ? "border-secondary shadow-secondary-transparent" : ""}`}
              onClick={() => handleClick(option.id)}
            >
              <div className="flex gap-2 items-center">
                <div
                  className={`w-12 h-12  rounded-[10px] flex items-center justify-center ${option.id === "product_llm" ? "bg-secondary-transparent" : "bg-success-50"}`}
                >
                  {option.icon}
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base text-gray-800">
                    {option.name}
                  </h3>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isByoLlm
              ? "grid-rows-[1fr] opacity-100"
              : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-1.5 bg-white rounded-lg px-6 py-4 border border-gray-200 w-full translate-y-0 transition-all duration-500">
              <h4 className="text-gray-800 font-semibold text-base">
                Enter API Key
              </h4>
              {keyDisplayMode === "masked" && savedKeyLast4 && (
                <p className="text-xs text-gray-500">
                  Key on file (ends in {savedKeyLast4}). Focus the field or
                  paste to replace.
                </p>
              )}

              <div className="flex gap-2 items-center w-full">
                <div className="w-[75%]">
                  <CommonInput
                    label="LLM API Key"
                    placeholder="e.g. sk-xxxxxxxxxxxxxxxx"
                    value={apiKeyInputValue}
                    onChange={handleKeyChange}
                    onFocus={handleKeyFocus}
                    type={inputType}
                    editable={!(keyDisplayMode === "masked" && savedKeyLast4)}
                  />
                </div>

                <div className="w-[25%]">
                  <CommonSelect
                    options={LLM_PROVIDERS?.map((provider) => ({
                      label: provider.disabled
                        ? `${provider.name} (soon)`
                        : provider.name,
                      value: provider.id,
                      disabled: !!provider.disabled,
                    }))}
                    placeholder="Select Provider"
                    onChange={(value) => setSelectedProvider(value)}
                    value={selectedProvider}
                    classNames={{
                      base: "bg-white w-[142px]",
                      mainWrapper: "w-[142px]",
                      trigger: "bg-white border border-gray-300 shadow-none",
                      popoverContent: "!min-w-[142px]",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <PrimaryButton
          children="Continue"
          startContent={false}
          endContent={<ArrowRight size={16} color="#fff" />}
          onClick={handleContinue}
          disabled={isPending}
          isLoading={isPending}
        />
      </div>
    </div>
  );
};

export default LLMSelection;
